import { useRef, useState } from 'react';
import { useUrlParam } from '../../../hooks/useUrlParam';
import type { TaskTypeId } from '../../../lib/taskTypes';
import { PAGE_BUILD_CLIENT_NAME, PAGE_BUILD_SUMMARY, pageBuildJobAt } from './fixtures/pageBuild';
import { SECURITY_AUDIT_CLIENT_NAME, securityAuditJobAt, securityAuditSummaryAt } from './fixtures/securityAudit';
import type { KeywordOverride, SectionsOverride } from '../../../lib/aiJobSummary';
import { SegmentedControl } from '../../ui';
import {
  sliceSummaryForStage,
  operatorStageForStatus,
  summaryChangesSince,
  type BuildSummary,
  type SummaryVersion,
} from '../../../lib/aiJobSummary';
import JobOperatorPanel, { type OperatorPanelActions } from './JobOperatorPanel';
import { PETES_LOOM_CLIENT_NAME, PETES_LOOM_JOB_ID, PETES_LOOM_SUMMARY, petesLoomJobAt, type PetesLoomGalleryStage } from './fixtures/petesLoom';

/** Fixture history: the build summary was written twice, so the Versions tab
    and the changed-since-last highlights have something to show. */
function galleryVersions(stage: PetesLoomGalleryStage): SummaryVersion[] {
  const spec = PETES_LOOM_SUMMARY.spec!;
  const build = PETES_LOOM_SUMMARY.build!;
  const approval = PETES_LOOM_SUMMARY.approval!;
  const earlierBuild: BuildSummary = {
    ...build,
    deviations: [],
    checks: build.checks.map((c) =>
      c.request === 'R7' ? { ...c, after: 'Mark ready turns the row pink and sorts it up. No badge yet, no count in the header.' } : c
    ),
  };
  const row = (id: string, s: SummaryVersion['stage'], job_version: number, summary: SummaryVersion['summary'], created_at: string): SummaryVersion => ({
    id,
    job_id: PETES_LOOM_JOB_ID,
    stage: s,
    job_version,
    summary,
    written_by: 'Owlbert',
    created_at,
  });
  const all: SummaryVersion[] = [
    row('v-spec-2', 'spec', 2, spec, '2026-09-09T18:10:00.000Z'),
    row('v-build-3a', 'build', 3, earlierBuild, '2026-09-09T18:31:00.000Z'),
    row('v-build-3b', 'build', 3, build, '2026-09-09T18:40:00.000Z'),
    row('v-approval-4', 'approval', 4, approval, '2026-09-09T20:21:00.000Z'),
  ];
  if (stage === 'spec_ready') return all.filter((v) => v.stage === 'spec');
  if (stage === 'build_ready') return all.filter((v) => v.stage !== 'approval');
  return all;
}

/**
 * Agents → Task types → a type: the operator view on a sample job of that type at each stage, inline and read-only.
 * No database, no Trello, no writes: every action is inert. The stage is ?stage= in the URL.
 */

const STAGES: { id: PetesLoomGalleryStage; label: string }[] = [
  { id: 'spec_ready', label: 'Spec ready' },
  { id: 'build_ready', label: 'Build ready' },
  { id: 'complete', label: 'Complete' },
];

const noop = () => {};

function useInertActions(): OperatorPanelActions {
  const [reply, setReply] = useState('');
  const [handoffDraft, setHandoffDraft] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  const noteFileRef = useRef<HTMLInputElement>(null);
  const replyFileRef = useRef<HTMLInputElement>(null);
  return {
    reply,
    setReply,
    handoffDraft,
    setHandoffDraft,
    noteDraft,
    setNoteDraft,
    busy: false,
    error: null,
    uploading: null,
    confirmCancel: false,
    setConfirmCancel: noop,
    confirmCancelBuild: false,
    setConfirmCancelBuild: noop,
    confirmDelete: false,
    setConfirmDelete: noop,
    confirmSubmit: false,
    setConfirmSubmit: noop,
    noteFileRef,
    replyFileRef,
    onPaste: () => noop,
    onFilePick: () => noop,
    onSaveNote: noop,
    onToggleAutoApprove: noop,
    onRevise: noop,
    onApproveSpec: noop,
    onCancel: noop,
    onApproveBuild: noop,
    onCancelBuild: noop,
    onDelete: noop,
    onSendBack: noop,
    onSendBackToBuild: noop,
    onSubmitted: noop,
  };
}

export default function OperatorDesignGallery({ typeId = 'general' }: { typeId?: TaskTypeId }) {
  const [stageParam, setStageParam] = useUrlParam('stage');
  const stage: PetesLoomGalleryStage = STAGES.some((s) => s.id === stageParam) ? (stageParam as PetesLoomGalleryStage) : 'spec_ready';
  const setStage = (s: PetesLoomGalleryStage) => setStageParam(s === 'spec_ready' ? null : s);
  const actions = useInertActions();
  const pageBuild = typeId === 'page_build';
  const audit = typeId === 'security_audit';
  // Security Audit answers can be clicked in the preview; like a section edit, they live in this page only.
  const [auditAnswers, setAuditAnswers] = useState<Record<string, string | null>>({});
  // Page Build sections can be edited in the preview; the edit lives in this page only, like a real save would.
  const [sectionsOverride, setSectionsOverride] = useState<SectionsOverride | null>(null);
  const [keywordOverride, setKeywordOverride] = useState<KeywordOverride | null>(null);
  const previewActions = pageBuild
    ? {
        ...actions,
        onSaveSections: async (sections: Parameters<NonNullable<typeof actions.onSaveSections>>[0]) =>
          setSectionsOverride(sections ? { sections, updated_at: new Date().toISOString() } : null),
        onSaveKeyword: async (kw: { primary: string; secondary: string[] } | null) =>
          setKeywordOverride(kw ? { primary: kw.primary.trim(), secondary: kw.secondary.map((s) => s.trim()).filter(Boolean), updated_at: new Date().toISOString() } : null),
      }
    : audit
      ? { ...actions, onAnswerDecision: async (id: string, answer: string | null) => setAuditAnswers((a) => ({ ...a, [id]: answer })) }
      : actions;
  const base = pageBuild ? pageBuildJobAt(stage) : audit ? securityAuditJobAt(stage) : petesLoomJobAt(stage);
  const sliced = sliceSummaryForStage(pageBuild ? PAGE_BUILD_SUMMARY : audit ? securityAuditSummaryAt(stage) : PETES_LOOM_SUMMARY, operatorStageForStatus(base.status));
  const summary =
    pageBuild && sliced.spec
      ? { ...sliced, spec: { ...sliced.spec, sections_override: sectionsOverride, keyword_override: keywordOverride } }
      : audit && sliced.spec
        ? { ...sliced, spec: { ...sliced.spec, decisions: sliced.spec.decisions.map((d) => (d.id in auditAnswers ? { ...d, answer: auditAnswers[d.id] } : d)) } }
        : sliced;
  const job = { ...base, operator_summary: summary };
  const versions = pageBuild || audit ? [] : galleryVersions(stage);
  const changes = summaryChangesSince(job, versions);
  const clientName = pageBuild ? PAGE_BUILD_CLIENT_NAME : audit ? SECURITY_AUDIT_CLIENT_NAME : PETES_LOOM_CLIENT_NAME;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <div className="t-eyebrow" style={{ marginBottom: '4px' }}>
            Design preview · sample job
          </div>
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
            {pageBuild ? (
              <>A made-up Templated Content Fix for {clientName} at three stages. At Spec ready you can edit any section, the way you would on a real job; edits stay on this page.</>
            ) : audit ? (
              <>The {clientName} security sweep of Oct 23 told as one audit, at three stages. At Spec ready you can answer the findings; answers stay on this page.</>
            ) : (
              <>
                Job <span className="t-num">e2c2c33f</span> for {clientName}, rendered inline and read-only at three stages.
              </>
            )}{' '}
            Nothing here reads or writes the database.
          </div>
        </div>
        <SegmentedControl options={STAGES} value={stage} onChange={(id) => setStage(id as PetesLoomGalleryStage)} />
      </div>

      <div
        style={{
          height: 'calc(100dvh - 220px)',
          minHeight: '640px',
          border: '1px solid var(--border-hairline)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-pop)',
          overflow: 'hidden',
          background: 'var(--surface-canvas)',
        }}
      >
        <JobOperatorPanel
          key={stage}
          job={job}
          summary={summary}
          traces={[]}
          piece={null}
          actions={previewActions}
          sectionEdits={(pageBuild || audit) && stage === 'spec_ready'}
          onClose={noop}
          versions={versions}
          changes={changes}
          clientName={clientName}
          inline
          readOnly
        />
      </div>

      <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }} className="t-num">
        Spec ready = spec only · Build ready = spec + build · Complete = spec + build + approval (approval markdown is a gallery sample).
      </p>
    </div>
  );
}
